import { GenericError, useAuth0 } from '@auth0/auth0-react';
import * as Sentry from '@sentry/react';
import {
  createFileRoute,
  invariant,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import { CookieBanner } from 'src/components';
import { IS_DEVELOPMENT } from 'src/config';
import {
  AccessDeniedError,
  EmailNotVerifiedError,
  DomainNotForwardedError,
  DomainNotVerifiedInOrgError,
  LoginValidatorError,
  MultipleCandidatesError,
  OrganizationNotFoundError,
  SAMLEmailNotFoundError,
  SocialAccountNotFoundError,
  SocialLoginConflictError,
  SSOLoginRequiredError,
} from 'src/utils/errors';
import { LogCategory } from 'src/utils/logger.categories';
import { sanitizeRedirectUrl } from 'src/utils/sanitizeRedirectUrl';
import { WHITELISTED_ACCOUNT_LINKING_PAGES_FOR_RESTRICTED_ACCOUNTS } from 'src/utils/urls/urls';
import { userHelpers } from 'src/utils/userHelpers';

export const Route = createFileRoute('/_auth')({
  async beforeLoad({ context, location }) {
    const auth = await context.auth; // Wait for the auth context to be ready

    // ========================================================================
    // Auth0 error handling
    // ========================================================================

    if (auth.error) {
      const searchParams = new URLSearchParams(location.searchStr);
      const errorParam = searchParams.get('error');
      const errorDescriptionParam = searchParams.get('error_description');
      Sentry?.addBreadcrumb({
        category: 'Auth',
        message: `[auth-guard/auth0-error]: ${[auth.error?.name, errorParam]
          .filter(Boolean)
          .join(' | ')}`,
        data: {
          ...auth.error,
          errorParam,
          errorDescriptionParam,
        },
      });

      const error =
        auth.error instanceof GenericError
          ? auth.error.error
          : (errorParam ?? 'Unknown authentication error');

      if (errorDescriptionParam === 'login_validator_error') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'The login validator service returned an error.',
        });
        throw new LoginValidatorError();
      }

      if (errorDescriptionParam === 'saml_email_not_found') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'SAML email not found, redirecting to login with prompt=login',
        });
        throw new SAMLEmailNotFoundError();
      }

      if (errorDescriptionParam === 'multiple_candidates') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'Multiple accounts found for linking',
        });
        throw new MultipleCandidatesError();
      }

      if (errorDescriptionParam === 'organization_not_found') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'Organization not found for this connection name',
        });
        throw new OrganizationNotFoundError();
      }

      if (errorDescriptionParam === 'domain_not_forwarded') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'Domain not forwarded so user have to create an account before logging in via SSO',
        });
        throw new DomainNotForwardedError();
      }

      if (errorDescriptionParam === 'domain_not_verified_in_org') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'Domain not verified for this organization',
        });
        throw new DomainNotVerifiedInOrgError();
      }

      if (errorDescriptionParam?.includes('conflict_')) {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: `Account conflict: ${errorDescriptionParam}`,
        });
        throw new SocialLoginConflictError(
          errorDescriptionParam.split('conflict_')[1] || undefined
        );
      }

      if (errorDescriptionParam?.includes('no_')) {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: `Connected account not found: ${errorDescriptionParam}`,
        });
        throw new SocialAccountNotFoundError(
          errorDescriptionParam.split('no_')[1] || undefined
        );
      }

      // Capture "https://errors.cloud.remarkable.com/auth/sso_login_required_error" and other errors that only report through the error message
      if (auth.error.message.toLowerCase() === 'sso_login_required_error') {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'User not authenticated, redirecting to login',
        });

        throw new SSOLoginRequiredError();
      }

      if ([error, errorParam].includes('login_required')) {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message: 'User not authenticated, redirecting to login',
        });
        throw redirect({
          to: '/login',
          search: {
            login_hint: auth.user && userHelpers.getEmail(auth.user),
            redirect: location.href,
          },
        });
      }

      if ([error, errorParam].includes('access_denied')) {
        Sentry?.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'User does not have access to this page, redirecting to /access-denied',
        });
        throw new AccessDeniedError();
      }

      /*
          Handle Auth0 'invalid state' error by first doing a no-prompt login
          attempt, then doing a regular login attempt, and finally redirecting
          to an error page if the issue persists.


          Reference:
          https://community.auth0.com/t/invalid-state-on-reload-auth0-callback-url-using-auth0-spa-js-and-angular-8/36469/9

          "[...] In summary, before a login transaction starts auth0-spa-js
          library creates a random state variable and stores it in a cookie.
          Then, it makes a redirect to Auth0 server with this state parameter.
          After the user is authenticated Auth0 returns a code along with the
          same state parameter. Auth-spa-js library checks if the state returned
          matches with the state stored in the cookie and if it matches makes a
          code exchange call to get the issued tokens.

          auth0-spa-js deletes the state cookie just before the code exchange
          request. If the user refreshes the browser before the code exchange
          completes, same code block in the library runs again but this time due
          to the state cookie being deleted, library throws a state mismatch
          error. [...]"

          ---

          It is unclear exactly why we trigger this error, it could be that we
          have some race conditions that interferes with Auth0, but until we
          can figure out the root cause, we will just attempt to handle the
          error when it occurs.
          */
      const loginAttemptsCookie = 'login_attempts=';
      const currentPath = location.href;

      if (
        auth.error.name === 'Error' &&
        auth.error.message === 'Invalid state'
      ) {
        // We need to keep track of the number of login attempts
        // to prevent an infinite loop of login attempts
        const cookies = document.cookie.split(';');
        let loginAttempts = Number(
          cookies
            .find((cookie) => cookie.includes(loginAttemptsCookie))
            ?.split('=')[1]
        );
        loginAttempts = isNaN(loginAttempts) ? 0 : loginAttempts;

        if (loginAttempts < 2) {
          document.cookie = `${loginAttemptsCookie}${loginAttempts + 1};`;
          Sentry.addBreadcrumb({
            category: LogCategory.Auth,
            message: `Login attempt ${
              loginAttempts + 1
            } for invalid state error`,
          });

          throw redirect({
            to: '/login',
            search: {
              login_hint: auth.user && userHelpers.getEmail(auth.user),
              prompt: loginAttempts === 0 ? 'none' : undefined,
              redirect: currentPath,
            },
          });
        } else {
          document.cookie = `${loginAttemptsCookie}0;`;

          Sentry.addBreadcrumb({
            category: LogCategory.Auth,
            message: 'Too many login attempts for invalid state error',
          });

          throw redirect({ to: '/', replace: true });
        }
      }

      // Reset login-attempts cookie
      document.cookie = `${loginAttemptsCookie}0;`;

      Sentry.withScope((scope) => {
        scope.setTags({
          'rm.is_auth_error': true,
          'rm.auth_error_url_param': errorParam,
          'rm.auth_error_url_description_param': errorDescriptionParam,
        });
        scope.captureException(auth.error);
      });

      throw redirect({
        to: '/authentication-error',
        search: {
          error,
          error_description: errorDescriptionParam ?? undefined,
          redirect_path: currentPath,
        },
      });
    }

    // ========================================================================
    // Auth0 authentication
    // ========================================================================

    if (!auth.isAuthenticated) {
      Sentry?.addBreadcrumb({
        category: 'Auth',
        message: 'User not authenticated, navigating to login',
      });
      throw redirect({
        to: '/login',
        search: {
          login_hint: auth.user && userHelpers.getEmail(auth.user),
          redirect: sanitizeRedirectUrl(location.href),
        },
      });
    } else {
      Sentry?.addBreadcrumb({
        category: 'Auth',
        message: 'User authenticated',
      });
    }

    invariant(auth.user, '[Auth]: User does not exist');

    // ========================================================================
    // Email verification
    // ========================================================================

    if (
      !userHelpers.isEmailVerified(auth.user) &&
      !userHelpers.isSAMLUser(auth.user)
    ) {
      throw new EmailNotVerifiedError();
    }

    // ========================================================================
    // Organization login
    // ========================================================================

    // Sometimes the user can come into a state where they have not properly logged in
    // with organization access. This could happen when the login is not initiated with the
    // orgId or the saml-error login flow is not triggered. The result is that they dont
    // see enterprise and the UI will be misleading without any pointer on how to fix it.

    const isSamlUserInLinkingProcess = userHelpers.isLinkable(auth.user);
    const isLegacyUserInLinkingProcess =
      userHelpers.isDomainClaimedByConnection(auth.user);
    const isCurrentlyLinkingAccounts = !!context.accountLinkingToken;
    const isUserOrgMemberButNotLoggedIn =
      userHelpers.isOrganizationMember(auth.user) &&
      !userHelpers.isLoggedInToOrganization(auth.user);

    if (
      !isSamlUserInLinkingProcess &&
      !isLegacyUserInLinkingProcess &&
      !isCurrentlyLinkingAccounts &&
      isUserOrgMemberButNotLoggedIn
    ) {
      Sentry?.addBreadcrumb({
        category: LogCategory.Auth,
        message:
          'User is org member but not logged in with org access, triggering silent org login...',
        data: IS_DEVELOPMENT
          ? {
              currentLocation: sanitizeRedirectUrl(location.href),
            }
          : undefined,
      });

      throw redirect({
        to: '/login',
        search: {
          prompt: 'none',
          redirect: sanitizeRedirectUrl(location.href),
          organization: userHelpers.getOrganizationId(auth.user),
        },
      });
    }

    // ========================================================================
    // Account linking
    // ========================================================================

    if (
      userHelpers.isAccountRestrictedToAccountLinking(auth.user) &&
      !isCurrentlyLinkingAccounts
    ) {
      const isOnWhitelistedPage =
        WHITELISTED_ACCOUNT_LINKING_PAGES_FOR_RESTRICTED_ACCOUNTS.findIndex(
          (path) => path && location.pathname.startsWith(path)
        ) > 0;

      if (!isOnWhitelistedPage) {
        if (userHelpers.hasActiveEnterpriseEnrollment(auth.user)) {
          Sentry.addBreadcrumb({
            category: LogCategory.Auth,
            message:
              'User has active enterprise enrollment, redirecting to enrollment',
          });
          throw redirect({
            to: '/enterprise/enroll',
            replace: true,
          });
        }

        Sentry.addBreadcrumb({
          category: LogCategory.Auth,
          message:
            'User is restricted to account linking, redirecting to account linking',
        });
        throw redirect({
          to: '/account/migrate/saml',
          replace: true,
        });
      }
    }
  },

  component: () => {
    const auth = useAuth0();
    return (
      <>
        <Outlet />
        {!auth.isLoading && auth.isAuthenticated && <CookieBanner />}
      </>
    );
  },
});
